// bookscript.js
// Assuming apiKey is defined and imported from 'apikey.js'
import { apiKey } from "./apikey.js";

const OPENAI_BASE_URL = "https://api.openai.com/v1";

const jsonHeaders = {
  Authorization: `Bearer ${apiKey}`,
  "Content-Type": "application/json"
};

/**
 * Safely aggregate all text from a Responses API payload.
 * Handles multiple output items and content parts.
 */
function extractTextFromResponse(data) {
  if (!data) return "";

  // Some SDKs add this convenience field; check just in case.
  if (typeof data.output_text === "string") {
    return data.output_text.trim();
  }

  const chunks = [];

  if (Array.isArray(data.output)) {
    for (const item of data.output) {
      const content = item && Array.isArray(item.content) ? item.content : [];
      for (const part of content) {
        if (part && part.type === "output_text" && typeof part.text === "string") {
          chunks.push(part.text);
        }
      }
    }
  }

  return chunks.join("\n\n").trim();
}

/**
 * Core helper using the Responses API + gpt-5.1
 *
 * Signature is backward-compatible with your original:
 *   generateContent(role, prompt, "gpt-5.1")
 * but you can now also call:
 *   generateContent(role, prompt, { model, reasoningEffort, verbosity, signal })
 */
async function generateContent(role, prompt, modelOrOptions = "gpt-5.1") {
  let model = "gpt-5.1";
  let reasoningEffort = "none";   // "none" | "low" | "medium" | "high"
  let verbosity = "medium";       // "low" | "medium" | "high"
  let signal;

  if (typeof modelOrOptions === "string") {
    if (modelOrOptions) model = modelOrOptions;
  } else if (modelOrOptions && typeof modelOrOptions === "object") {
    if (modelOrOptions.model) model = modelOrOptions.model;
    if (modelOrOptions.reasoningEffort) reasoningEffort = modelOrOptions.reasoningEffort;
    if (modelOrOptions.verbosity) verbosity = modelOrOptions.verbosity;
    if (modelOrOptions.signal) signal = modelOrOptions.signal;
  }

  const res = await fetch(`${OPENAI_BASE_URL}/responses`, {
    method: "POST",
    headers: jsonHeaders,
    signal,
    body: JSON.stringify({
      model,
      // High-level system/dev instructions
      instructions: role || "You are a helpful writing assistant.",
      // Main user input
      input: String(prompt || ""),
      // GPT-5.1 reasoning + verbosity controls
      reasoning: { effort: reasoningEffort },
      text: { verbosity }
    })
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const errMsg =
      (data && data.error && data.error.message) ||
      `${res.status} ${res.statusText}`;
    throw new Error(`OpenAI text error: ${errMsg}`);
  }

  const text = extractTextFromResponse(data);
  if (!text) {
    throw new Error("OpenAI text error: Empty response.");
  }
  return text;
}

// ---- HIGH-LEVEL HELPERS ----

async function generateConcept({
  gptRole,
  bookLength,
  genre,
  keywords,
  model = "gpt-5.1",
  reasoningEffort = "low",
  verbosity = "medium"
}) {
  const prompt = [
    `You are helping outline a book.`,
    `Generate a high-level concept for a ${genre} book.`,
    `Target length: about ${bookLength} words in total (not just this answer).`,
    `Use these keywords: ${keywords.join(", ")}.`,
    `Return 1–2 tight paragraphs describing:`,
    `- the premise`,
    `- main character(s)`,
    `- central conflict`,
    `- tone and target audience.`
  ].join(" ");

  return await generateContent(gptRole, prompt, { model, reasoningEffort, verbosity });
}

async function generateTableOfContents({
  gptRole,
  genre,
  keywords,
  concept,
  model = "gpt-5.1",
  reasoningEffort = "low",
  verbosity = "low"
}) {
  const prompt = [
    `You are outlining the full book structure.`,
    `Genre: ${genre}.`,
    `Concept: "${concept}".`,
    `Keywords: ${keywords.join(", ")}.`,
    `Generate a numbered table of contents with clear chapter titles.`,
    `Group chapters into acts if appropriate.`,
    `Return as a simple text list (one chapter per line).`
  ].join(" ");

  return await generateContent(gptRole, prompt, { model, reasoningEffort, verbosity });
}

async function generateChapters({
  gptRole,
  genre,
  keywords,
  tableOfContents,
  autoGen,
  concept = "",
  model = "gpt-5.1",
  reasoningEffort = "medium",
  verbosity = "high"
}) {
  const chapters = String(tableOfContents || "")
    .split("\n")
    .map(line => line.trim())
    .filter(Boolean);

  const chaptersContent = [];

  for (let i = 0; i < chapters.length; i++) {
    const chapterTitle = chapters[i];

    const prompt = [
      `You are writing a ${genre} book in polished, publication-ready prose.`,
      concept ? `Overall concept: "${concept}".` : "",
      `Chapter title: "${chapterTitle}".`,
      `Keywords to weave in naturally: ${keywords.join(", ")}.`,
      `Write the full chapter content in a consistent voice and point of view.`,
      `Use clear pacing, strong scene descriptions, and natural dialogue where appropriate.`,
      `Do NOT include the words "Chapter" or the chapter title in the body; just output the story text.`
    ]
      .filter(Boolean)
      .join(" ");

    const chapterContent = await generateContent(gptRole, prompt, {
      model,
      reasoningEffort,
      verbosity
    });

    chaptersContent.push({ title: chapterTitle, content: chapterContent });

    if (!autoGen) break; // only first chapter if autoGen is false
  }

  return chaptersContent;
}

// ---- SMALL TEXT UTILITIES ----
function truncateText(s = "", maxChars = 280) {
  const clean = String(s).replace(/\s+/g, " ").trim();
  return clean.length <= maxChars ? clean : clean.slice(0, maxChars - 1).trim() + "…";
}

function topKeywords(list = [], n = 5) {
  return (Array.isArray(list) ? list : String(list).split(","))
    .map(x => String(x).trim())
    .filter(Boolean)
    .slice(0, n);
}

/**
 * Returns a compact, high-signal prompt (< ~300 chars) with sensible defaults.
 * Example output:
 * Book cover for "Skybound" — fantasy. Include: dragon, castle, night. One clear focal scene, legible title space, rule-of-thirds, cohesive palette.
 * | size:1024x1536 | quality:hd | style: painted realism, cinematic lighting
 */
function generateImagePrompt({ title, genre, keywords = [], concept = "" }) {
  const t = String(title || "Untitled").trim();
  const g = String(genre || "fiction").trim();
  const kws = topKeywords(keywords, 5);
  const conceptLine = truncateText(concept, 140); // 1 short line max

  const bits = [
    `Book cover for "${t}" — ${g}.`,
    kws.length ? `Include: ${kws.join(", ")}.` : "",
    conceptLine ? `Motif: ${conceptLine}` : "",
    "One clear focal scene, legible title space, rule-of-thirds, cohesive palette."
  ].filter(Boolean);

  // Still output the inline controls so your image helper can parse them.
  return `${bits.join(" ")} | size:1024x1536 | quality:hd | style: painted realism, cinematic lighting`;
}

// ---- Generate cover image URL via Images API (now default gpt-image-1) ----
async function generateCoverImageUrl({
  imagePrompt,             // string (can include inline " | size: | quality: | style:")
  model = "gpt-image-1",   // recommended latest image model
  size = "1792x1024",
  quality = "hd",
  n = 1,
  style = ""
}) {
  // Support inline overrides like "… | size:1920x1080 | n:2 | style: watercolor"
  const parsed = (() => {
    const out = { prompt: imagePrompt, size, quality, n, style };
    if (!imagePrompt) return out;

    const [promptOnly, ...rest] = imagePrompt.split("|").map(s => s.trim());
    out.prompt = promptOnly || imagePrompt;

    for (const part of rest) {
      const [kRaw, ...r] = part.split(":");
      const k = (kRaw || "").toLowerCase().trim();
      const v = (r.join(":") || "").trim();
      if (!k || !v) continue;
      if (k === "size") out.size = v;
      else if (k === "quality") out.quality = v;
      else if (k === "n") out.n = Math.max(1, parseInt(v, 10) || 1);
      else if (k === "style") out.style = v;
    }
    return out;
  })();

  const fullPrompt = parsed.style
    ? `${parsed.prompt}\n\nStyle: ${parsed.style}`
    : parsed.prompt;

  const res = await fetch(`${OPENAI_BASE_URL}/images/generations`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({
      model,
      prompt: fullPrompt,
      n: parsed.n,
      size: parsed.size,
      quality: parsed.quality,
      response_format: "url"
    })
  });

  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`OpenAI image error: ${res.status} ${res.statusText} ${errText}`);
  }

  const data = await res.json();
  const urls = (data && Array.isArray(data.data) ? data.data : [])
    .map(it => it && it.url)
    .filter(Boolean);

  if (!urls.length) throw new Error("Image generation returned no URLs.");
  return urls[0]; // return the first; caller can adapt for multiple
}

// ---- EXPORTS (now includes image helpers) ----
function exportToTxt(chaptersHtml) {
  const element = document.createElement("a");
  element.setAttribute(
    "href",
    "data:text/plain;charset=utf-8," + encodeURIComponent(chaptersHtml)
  );
  element.setAttribute("download", "chapters.txt");
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
}

export {
  generateConcept,
  generateTableOfContents,
  generateChapters,
  generateImagePrompt,
  generateCoverImageUrl,
  exportToTxt
};
